import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../Firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './CommunityManager.css';

const API = process.env.REACT_APP_API_BASE_URL;

const STATUS_LABELS = {
  draft: { label: 'Draft', color: '#94a3b8' },
  pending: { label: 'Under Review', color: '#f59e0b' },
  in_review: { label: 'In Review', color: '#6366f1' },
  approved: { label: 'Active', color: '#22c55e' },
  rejected: { label: 'Rejected', color: '#ef4444' }
};

const CommunityCard = ({ c, onContinue }) => {
  const s = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
  const thumb = c.pics?.[0];
  return (
    <div className="cm-card">
      {thumb && <img src={thumb} alt="" className="cm-card-hero" />}
      {!thumb && <div className="cm-card-hero cm-card-hero--placeholder" />}
      <div className="cm-card-body">
        <div className="cm-card-top">
          <h3 className="cm-card-title">{c.title}</h3>
          <span className="cm-card-badge" style={{ background: s.color + '22', color: s.color }}>{s.label}</span>
        </div>
        {c.location && <p className="cm-card-meta">📍 {c.location}</p>}
        <p className="cm-card-meta">Updated {new Date(c.updatedAt).toLocaleDateString('en-GB')}</p>
        {c.description && <p className="cm-card-desc">{c.description.slice(0, 120)}{c.description.length > 120 ? '…' : ''}</p>}
        <div className="cm-card-actions">
          {c.status === 'draft' && (
            <button className="cm-btn cm-btn--primary" onClick={() => onContinue(c)}>Continue editing →</button>
          )}
          {c.status === 'approved' && (
            <Link className="cm-btn cm-btn--primary" to={`/${c.databaseRef}`}>View live page →</Link>
          )}
          {c.status === 'pending' && (
            <span className="cm-btn cm-btn--muted">Awaiting review</span>
          )}
        </div>
      </div>
    </div>
  );
};

const AccordionSection = ({ id, label, count, isOpen, onToggle, children }) => (
  <div className={`cm-accordion ${isOpen ? 'cm-accordion--open' : ''}`}>
    <button className="cm-accordion-header" onClick={() => onToggle(id)}>
      <span className="cm-accordion-label">{label}</span>
      {count > 0 && <span className="cm-accordion-count">{count}</span>}
      <span className="cm-accordion-chevron">{isOpen ? '▲' : '▼'}</span>
    </button>
    {isOpen && <div className="cm-accordion-body">{children}</div>}
  </div>
);

const CommunityManager = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [communities, setCommunities] = useState([]);
  const [fetchError, setFetchError] = useState('');
  const [openSection, setOpenSection] = useState('pending');
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return unsub;
  }, []);

  useEffect(() => {
    if (user) fetchCommunities();
  }, [user]);

  const fetchCommunities = async () => {
    setFetchError('');
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API}/community-builder/my-communities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const list = data.communities || [];
      setCommunities(list);
      if (list.length > 0) localStorage.setItem('cb_has_communities', 'true');
      // Auto-open the first non-empty section
      const byStatus = groupBy(list);
      if (byStatus.draft?.length) setOpenSection('draft');
      else if (byStatus.pending?.length || byStatus.in_review?.length) setOpenSection('pending');
      else if (byStatus.approved?.length) setOpenSection('approved');
    } catch (err) {
      setFetchError(err.message);
    }
  };

  const groupBy = (list) => ({
    draft: list.filter(c => c.status === 'draft'),
    pending: list.filter(c => c.status === 'pending' || c.status === 'in_review'),
    approved: list.filter(c => c.status === 'approved')
  });

  const toggle = (id) => setOpenSection(prev => prev === id ? null : id);

  const handleContinue = (community) => {
    navigate('/create', { state: { resumeDraftId: community._id } });
  };

  if (loading) return <div className="cm-loading">Loading...</div>;

  if (!user) return (
    <div className="cm-gate">
      <h2>Your Community Manager</h2>
      <p>Please <Link to="/login">log in</Link> to view and manage your communities.</p>
    </div>
  );

  const grouped = groupBy(communities);
  const hasCommunities = communities.length > 0;

  return (
    <div className="cm-root">
      {hasCommunities && (
        <div className="cm-header">
          <div className="cm-header-inner">
            <div className="cm-header-left">
              <span className="cm-header-icon">🏘️</span>
              <div>
                <p className="cm-header-title">Your communities</p>
                <p className="cm-header-sub">
                  {grouped.draft.length > 0 && `${grouped.draft.length} draft${grouped.draft.length > 1 ? 's' : ''} · `}
                  {grouped.pending.length > 0 && `${grouped.pending.length} under review · `}
                  {grouped.approved.length > 0 && `${grouped.approved.length} active`}
                </p>
              </div>
            </div>
            <Link to="/create" className="cm-header-cta">+ New community</Link>
          </div>
        </div>
      )}

      <div className="cm-content">
        <div className="cm-page-header">
          <h1 className="cm-page-title">Community Manager</h1>
          {!hasCommunities && (
            <Link to="/create" className="cm-btn cm-btn--primary">Build your first community →</Link>
          )}
        </div>

        {fetchError && <div className="cm-error">{fetchError}</div>}

        {hasCommunities ? (
          <div className="cm-sections">
            <AccordionSection
              id="draft"
              label="Drafts"
              count={grouped.draft.length}
              isOpen={openSection === 'draft'}
              onToggle={toggle}
            >
              {grouped.draft.length === 0 ? (
                <p className="cm-empty">No drafts yet. <Link to="/create">Start building →</Link></p>
              ) : (
                <div className="cm-cards">
                  {grouped.draft.map(c => <CommunityCard key={c._id} c={c} onContinue={handleContinue} />)}
                </div>
              )}
            </AccordionSection>

            <AccordionSection
              id="pending"
              label="Submitted"
              count={grouped.pending.length}
              isOpen={openSection === 'pending'}
              onToggle={toggle}
            >
              {grouped.pending.length === 0 ? (
                <p className="cm-empty">Nothing under review yet.</p>
              ) : (
                <div className="cm-cards">
                  {grouped.pending.map(c => <CommunityCard key={c._id} c={c} onContinue={handleContinue} />)}
                </div>
              )}
            </AccordionSection>

            <AccordionSection
              id="approved"
              label="Active"
              count={grouped.approved.length}
              isOpen={openSection === 'approved'}
              onToggle={toggle}
            >
              {grouped.approved.length === 0 ? (
                <p className="cm-empty">No active communities yet.</p>
              ) : (
                <div className="cm-cards">
                  {grouped.approved.map(c => <CommunityCard key={c._id} c={c} onContinue={handleContinue} />)}
                </div>
              )}
            </AccordionSection>
          </div>
        ) : (
          !fetchError && (
            <div className="cm-empty-state">
              <div className="cm-empty-icon">🏗️</div>
              <h2>No communities yet</h2>
              <p>Use the AI builder to create your first community in seconds.</p>
              <Link to="/create" className="cm-btn cm-btn--primary cm-btn--lg">Build with AI →</Link>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CommunityManager;
